import {Component, Input, OnChanges} from '@angular/core';
import {NgClass, NgFor, NgIf} from '@angular/common';
import {MatTooltip} from "@angular/material/tooltip";

type NodeKind = 'primitive' | 'open' | 'array-inline'
type ValueType = 'string' | 'number' | 'boolean' | 'null'

interface InlineItem {
  value: any
  valueType: ValueType
}

interface FlatNode {
  kind: NodeKind
  key: string | null
  keyType: 'property' | 'index'
  depth: number
  path: string
  ancestors: string[]
  containerType?: 'object' | 'array'
  childCount?: number
  value?: any
  valueType?: ValueType
  inlineItems?: InlineItem[]
}

@Component({
  selector: 'app-json-visualizer',
  standalone: true,
  imports: [NgFor, NgIf, NgClass, MatTooltip],
  templateUrl: './json-visualizer.component.html',
  styleUrl: './json-visualizer.component.scss'
})
export class JsonVisualizerComponent implements OnChanges {
  @Input() data: any
  @Input() label = 'Data'

  allNodes: FlatNode[] = []
  visibleNodes: FlatNode[] = []
  collapsed = new Set<string>()
  copied = false
  copiedId = ''

  ngOnChanges(): void {
    this.allNodes = this.buildRoot(this.data)
    this.initCollapsed()
    this.updateVisible()
  }

  private buildRoot(data: any): FlatNode[] {
    if (data == null || data === '') return []
    const type = this.getType(data)

    if (type === 'object') {
      const nodes: FlatNode[] = []
      const entries = Object.entries(data).sort(([a], [b]) => a.localeCompare(b))
      entries.forEach(([k, v], i) => {
        nodes.push(...this.buildNodes(v, `root.${k}`, [], 0, k, 'property'))
      })
      return nodes
    }

    if (type === 'array') {
      const arr = data as any[]
      if (this.allPrimitive(arr)) {
        return [{
          kind: 'array-inline', key: null, keyType: 'property',
          depth: 0, path: 'root', ancestors: [], childCount: arr.length,
          inlineItems: arr.map(v => ({ value: v, valueType: this.getType(v) as ValueType }))
        }]
      }
      const nodes: FlatNode[] = []
      arr.forEach((v, i) => {
        nodes.push(...this.buildNodes(v, `root[${i}]`, [], 0, String(i + 1), 'index'))
      })
      return nodes
    }

    return [{ kind: 'primitive', key: null, keyType: 'property', value: data, valueType: type as ValueType, depth: 0, path: 'root', ancestors: [] }]
  }

  private buildNodes(value: any, path: string, ancestors: string[], depth: number, key: string | null, keyType: 'property' | 'index'): FlatNode[] {
    const nodes: FlatNode[] = []
    const type = this.getType(value)

    if (type === 'object') {
      const entries = Object.entries(value).sort(([a], [b]) => a.localeCompare(b))
      nodes.push({ kind: 'open', key, keyType, depth, path, ancestors, containerType: 'object', childCount: entries.length })
      entries.forEach(([k, v]) => {
        nodes.push(...this.buildNodes(v, `${path}.${k}`, [...ancestors, path], depth + 1, k, 'property'))
      })
    } else if (type === 'array') {
      const arr = value as any[]
      if (this.allPrimitive(arr)) {
        nodes.push({
          kind: 'array-inline', key, keyType, depth, path, ancestors, childCount: arr.length,
          inlineItems: arr.map(v => ({ value: v, valueType: this.getType(v) as ValueType }))
        })
      } else {
        nodes.push({ kind: 'open', key, keyType, depth, path, ancestors, containerType: 'array', childCount: arr.length })
        arr.forEach((v, i) => {
          nodes.push(...this.buildNodes(v, `${path}[${i}]`, [...ancestors, path], depth + 1, String(i + 1), 'index'))
        })
      }
    } else {
      nodes.push({ kind: 'primitive', key, keyType, value, valueType: type as ValueType, depth, path, ancestors })
    }

    return nodes
  }

  private allPrimitive(arr: any[]): boolean {
    return arr.every(v => v === null || (typeof v !== 'object' && !Array.isArray(v)))
  }

  private initCollapsed(): void {
    this.collapsed.clear()
    for (const node of this.allNodes) {
      if (node.kind === 'open' && node.depth >= 1) {
        this.collapsed.add(node.path)
      }
    }
  }

  toggle(path: string): void {
    this.collapsed.has(path) ? this.collapsed.delete(path) : this.collapsed.add(path)
    this.updateVisible()
  }

  expandAll(): void {
    this.collapsed.clear()
    this.updateVisible()
  }

  collapseAll(): void {
    for (const node of this.allNodes) {
      if (node.kind === 'open' && node.depth >= 0) this.collapsed.add(node.path)
    }
    this.updateVisible()
  }

  private updateVisible(): void {
    this.visibleNodes = this.allNodes.filter(node => !node.ancestors.some(a => this.collapsed.has(a)))
  }

  isCollapsed(path: string): boolean {
    return this.collapsed.has(path)
  }

  displayValue(value: any, valueType: ValueType | undefined): string {
    if (value == null || valueType === 'null') return 'null'
    const str = String(value)
    return str.length > 80 ? str.slice(0, 80) + '…' : str
  }

  async copyText(value: any, id: string): Promise<void> {
    const text = value == null ? 'null' : String(value)
    try {
      await navigator.clipboard.writeText(text)
      this.copiedId = id
      setTimeout(() => { if (this.copiedId === id) this.copiedId = '' }, 1500)
    } catch { /* ignore */ }
  }

  async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(JSON.stringify(this.data, null, 2))
      this.copied = true
      setTimeout(() => this.copied = false, 2000)
    } catch { /* ignore */ }
  }

  private getType(value: any): 'string' | 'number' | 'boolean' | 'null' | 'object' | 'array' {
    if (value === null) return 'null'
    if (Array.isArray(value)) return 'array'
    return typeof value as any
  }
}
